import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorMessage } from '@scoreboard/ui-web';
import { readAttribution } from '@/lib/acquisition';
import { errorMessage } from '@/lib/errors';
import { useSession } from '@/lib/session';

// Doc 02 §5.1 : cette page ne consomme JAMAIS le token au chargement — les scanners
// d'email d'entreprise brûleraient le lien à usage unique avant l'utilisateur.

export default function AuthVerifyRoute(): React.ReactElement {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { api, setAccount } = useSession();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = params.get('token');

  if (token === null || token.length === 0) {
    return (
      <div className="page page--narrow stack">
        <h1>{t('auth.verifyTitle')}</h1>
        <ErrorMessage message={t('auth.missingToken')} />
        <Link className="btn" to="/login">
          {t('auth.backToLogin')}
        </Link>
      </div>
    );
  }

  const onVerify = async (): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      // Boucle virale (doc 06 §7.3) : l'origine mémorisée à l'arrivée est rejouée
      // ici, le seul endroit du parcours où un compte peut naître. Le serveur ne
      // l'enregistre que s'il crée effectivement le compte — sinon elle est ignorée.
      const result = await api.verifyMagicLink(token, readAttribution() ?? undefined);
      setAccount(result.account);
      navigate('/games', { replace: true });
    } catch (err) {
      setError(errorMessage(t, err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="page page--narrow stack">
      <h1>{t('auth.verifyTitle')}</h1>
      <p className="muted">{t('auth.verifyBody')}</p>
      {error !== null ? (
        <>
          <ErrorMessage message={error} />
          <Link className="btn" to="/login">
            {t('auth.backToLogin')}
          </Link>
        </>
      ) : (
        <button
          type="button"
          className="btn btn--primary btn--lg"
          disabled={pending}
          onClick={() => void onVerify()}
        >
          {pending ? t('auth.verifying') : t('auth.verifyCta')}
        </button>
      )}
    </div>
  );
}
